/* eslint-disable prefer-destructuring */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { debounce } from '@mui/material/utils';
import { GridFilterItem } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { MutableRefObject, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { usePage } from '../../../contexts/usePageContext';
import { setConfigStorageItem } from '../../../hooks/useConfigStorage';
import { useCurrentStructure } from '../../../hooks/useCurrentStructure';
import { changeCurrentPreset } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { RootState } from '../../../redux/store';
import {
 syncFiltersFromPreset,
 updateUrlFilterParams,
} from '../XTable/urlFilterParams';
import { isEmptyFilterValue, toFormArrayValue } from '../XTable/filterModel';
import { FormValues } from '../../../types/types';
import { findStructure } from '../../../utils/dataTypes';
import { CustomGridApi } from '../XTable/types';

/**
 * Assign `value` into `target` along a dotted `path`, creating intermediate
 * objects. react-hook-form treats a dotted field name (`ResultProducts.Product`)
 * as a nested path, so the form values handed to `reset()` must be nested too —
 * a flat `{ 'ResultProducts.Product': … }` key would be invisible to the
 * Controller (which reads `target.ResultProducts.Product`) and would survive as
 * a stale empty sibling that later collides with the real value on submit.
 */
const setByPath = (
 target: Record<string, any>,
 path: string,
 value: unknown,
): void => {
 const parts = path.split('.');
 let cursor = target;
 for (let i = 0; i < parts.length - 1; i += 1) {
  const key = parts[i];
  if (
   typeof cursor[key] !== 'object' ||
   cursor[key] === null ||
   Array.isArray(cursor[key])
  ) {
   cursor[key] = {};
  }
  cursor = cursor[key];
 }
 cursor[parts[parts.length - 1]] = value;
};

const useFilterSidebar = ({
 apiRef,
}: {
 apiRef: MutableRefObject<CustomGridApi>;
}) => {
 const { pageName } = usePage();
 const dispatch = useDispatch();
 //  const allColumns = apiRef?.current?.getAllColumns();

 const currentColumnsPreset = useSelector(
  (state: RootState) =>
   state.clientConfiguration.currentColumnsPreset?.[pageName] || null,
 );
 const methods = useForm<Record<string, any>>();

 const structure = useCurrentStructure();

 const columns = apiRef?.current?.getAllColumns(); // useMemo(() => apiRef?.current?.getAllColumns(), [apiRef.current]);

 const isFormChanged = methods.formState.isDirty;

 const getValuesFromStorage = () => {
  const presetFields = currentColumnsPreset?.filters?.fields || {};
  // Build a *nested* object: RHF parses dotted names as nested paths, so fields
  // and operators have to be nested here to match how the Controllers read them.
  const normalizedFields = Object.entries(presetFields).reduce<Record<string, any>>(
   (acc, [fieldKey, fieldValue]) => {
    const currentStructure = findStructure(structure, fieldKey);

    if (currentStructure?.Type === 'datetime') {
     const dateString = typeof fieldValue === 'string' ? fieldValue : '';
     const [dateFromRaw, dateToRaw] = dateString ? dateString.split('-') : ['', ''];
     setByPath(acc, fieldKey, dateString);
     setByPath(acc, `dateRanges.${fieldKey}`, {
      from: dateFromRaw && dayjs(dateFromRaw).isValid() ? dayjs(dateFromRaw) : '',
      to: dateToRaw && dayjs(dateToRaw).isValid() ? dayjs(dateToRaw) : '',
      period: '',
      year: '',
      month: '',
      week: '',
     });
     return acc;
    }

    if (['model', 'array', 'enum'].includes(currentStructure?.Type || '')) {
     setByPath(acc, fieldKey, toFormArrayValue(fieldValue));
     return acc;
    }

    setByPath(acc, fieldKey, fieldValue || '');
    return acc;
   },
   {},
  );

  const presetOperators = currentColumnsPreset?.filters?.operators || {};
  const normalizedOperators = Object.entries(presetOperators).reduce<Record<string, any>>(
   (acc, [fieldKey, operatorValue]) => {
    setByPath(acc, fieldKey, operatorValue);
    return acc;
   },
   {},
  );

  return {
   ...normalizedFields,
   operators: normalizedOperators,
  };
 };
 function fieldsWithDots(obj: Record<string, any>): Record<string, any> {
  // https://github.com/react-hook-form/react-hook-form/issues/3351
  const out: { [key: string]: any } = {};

  function walk(x: Record<string, any>, prefix: string[] = []): void {
   if (x instanceof dayjs) {
    out[[...prefix].join('.')] = x;
   } else {
    Object.entries(x).forEach(([key, val]) => {
     if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      walk(val, [...prefix, key]);
     } else {
      out[[...prefix, key].join('.')] = val;
     }
    });
   }
  }

  walk(obj);
  return out;
 }
 const applyFilters = (data: FormValues) => {
  const { operators = {}, dateRanges = {}, ...fields } = data;
  const fixedOperators = fieldsWithDots(operators);
  const fixedFields = fieldsWithDots(fields);

  const allFilters = Object.entries(fixedFields).map(([key, value]) => {
   if (typeof value === 'undefined')
    return null;

   const currentStructure = findStructure(structure, key);
   if (currentStructure?.Type === 'datetime') return null;

   return {
    field: key,
    operator: fixedOperators[key],
    value,
   };
  }) as GridFilterItem[];

  const activeFilters = allFilters.filter(
   (f) => f !== null && !isEmptyFilterValue(f.value) && f.operator !== '',
  );

  const datetimeFieldKeys = Object.keys(fixedFields).filter(
   (fieldKey) => findStructure(structure, fieldKey)?.Type === 'datetime',
  );
  datetimeFieldKeys.forEach((fieldKey) => {
   const dateFrom = dateRanges?.[fieldKey]?.from;
   const dateTo = dateRanges?.[fieldKey]?.to;

   if (dateFrom && dayjs(dateFrom).isValid()) {
    activeFilters.push({
     field: fieldKey,
     operator: 'onOrAfter',
     value: dayjs(dateFrom).toDate(),
    });
   }

   if (dateTo && dayjs(dateTo).isValid()) {
    activeFilters.push({
     field: fieldKey,
     operator: 'onOrBefore',
     value: dayjs(dateTo).toDate(),
    });
   }
  });
  console.log('activeFilters', activeFilters);
  // send to datagrid
  apiRef.current.setFilterModel(
   {
    items: activeFilters.map((item) => ({
     ...item,
     id: `${item.field}-${JSON.stringify(item.value).replace(/[^a-zA-Z0-9]/g, '')}`,
    })),
   },
   'upsertFilterItems',
  );

  const newPresetFieldsObj = Object.keys(fixedFields).reduce<Record<string, any>>(
   (acc, fieldKey) => {
    const currentStructure = findStructure(structure, fieldKey);

    if (currentStructure?.Type === 'datetime') {
      const dateFrom = dateRanges?.[fieldKey]?.from;
      const dateTo = dateRanges?.[fieldKey]?.to;
      if (!dateFrom && !dateTo) {
       acc[fieldKey] = '';
       return acc;
      }

      const fromValue =
       dateFrom && dayjs(dateFrom).isValid()
        ? dayjs(dateFrom).format('YYYY.MM.DD HH:mm:ss')
        : 'x';
      const toValue =
       dateTo && dayjs(dateTo).isValid()
        ? dayjs(dateTo).format('YYYY.MM.DD HH:mm:ss')
        : 'x';

      acc[fieldKey] = `${fromValue}-${toValue}`;
      return acc;
    }

    acc[fieldKey] = fixedFields[fieldKey];
    return acc;
   },
   {},
  );

  console.log(newPresetFieldsObj);

  const preset = {
   ...currentColumnsPreset,
   name: currentColumnsPreset?.name || '',
   columns: currentColumnsPreset?.columns || [],
   filters: { fields: newPresetFieldsObj, operators: fixedOperators },
  };

  console.log('applyFilters', preset);

  dispatch(
   changeCurrentPreset({
    page: pageName,
    type: 'columns',
    preset,
    temp: true,
   }),
  );
  dispatch(
   changeCurrentPreset({
    page: pageName,
    type: 'columns',
    preset,
    temp: false,
   }),
  );
  // dispatch(getCurrentPresetStart({ table: pageName, type: 'columns' }));

  setConfigStorageItem({
   page: pageName,
   key: 'columns',
   value: preset,
  });

  updateUrlFilterParams(preset.filters ?? null);
 };

 const resetFilters = () => {
  const presetFieldKeys = Object.keys(currentColumnsPreset?.filters?.fields || {});
  const resetFields = presetFieldKeys.reduce<Record<string, any>>((acc, fieldKey) => {
   acc[fieldKey] = '';
   return acc;
  }, {});
  const resetOperators = presetFieldKeys.reduce<Record<string, string>>(
   (acc, fieldKey) => {
    acc[fieldKey] = '';
    return acc;
   },
   {},
  );
  const resetDateRanges = presetFieldKeys.reduce<Record<string, any>>(
   (acc, fieldKey) => {
    if (findStructure(structure, fieldKey)?.Type !== 'datetime') return acc;

    acc[fieldKey] = {
     from: '',
     to: '',
     period: '',
     year: '',
     month: '',
     week: '',
    };
    return acc;
   },
   {},
  );

  const resetValues = {
   ...resetFields,
   operators: resetOperators,
   dateRanges: resetDateRanges,
  };

  methods.reset(resetValues);
  applyFilters(resetValues);
  updateUrlFilterParams(null);
 };

 function getValue<T extends Record<string, any>>(obj: T, path: string): any {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj as any);
 }

 useEffect(() => {
  // activate operator if value is not empty and remove if
  const debouncedHandler = debounce((value, { name, type }) => {
   if (name && !name.includes('operators') && !name.includes('dateRanges')) {
    const currentValue = name.includes('.')
     ? getValue(value, name)
     : value?.[name];
    // Read the operator through the same nested path RHF stores it under;
    // a flat `value.operators['a.b']` lookup is always undefined for dotted
    // fields and would silently reset a user-chosen operator back to `equals`.
    const currentOperator = getValue(value?.operators || {}, name);
    console.log(currentValue, name);
    // is empty string, or empty object, or empty array
    const isEmpty =
     currentValue === undefined ||
     currentValue === '' ||
     (!!currentValue &&
      !Array.isArray(currentValue) &&
      typeof currentValue === 'object' &&
      Object.keys(currentValue).length === 0) ||
     (Array.isArray(currentValue) && currentValue?.length === 0);

    // console.log(
    //  currentValue === undefined,
    //  currentValue === '',
    //  !!currentValue &&
    //   !Array.isArray(currentValue) &&
    //   typeof currentValue === 'object' &&
    //   Object.keys(currentValue).length === 0,
    //  Array.isArray(currentValue) && currentValue?.length === 0,
    // );

    // #########################################################
    // REWRITE THIS
    // #########################################################

    // console.log(typeof currentValues, currentValues, value);

    // console.log(
    //  name,
    //  currentValue,
    //  isEmpty,
    //  currentValues?.[name],
    //  value?.[name],
    // );

    if (
     !isEmpty &&
     (currentOperator === '' || currentOperator === undefined)
    ) {
     console.log('empty operator');
     methods.setValue(`operators[${name}]`, 'equals'); // warning
    } else if (isEmpty) {
     console.log('empty value', `operators[${name}]`);
     methods.setValue(`operators[${name}]`, '');
    } else {
     console.log('not empty', `operators[${name}]`, currentOperator);
     methods.setValue(`operators[${name}]`, currentOperator || 'equals');
    }
   }
  }, 50);

  const subscription = methods.watch(debouncedHandler);

  return () => {
   subscription.unsubscribe();
  };
 }, [methods.watch]);

 useEffect(() => {
  const vals = getValuesFromStorage();

  if (vals) {
   methods.reset({ ...vals });
  }

  if (!apiRef?.current) return;

  // The sidebar derives the form + grid purely from the current preset.
  // useTableInit owns reading the URL and folding it into the preset, so by the
  // time this runs the preset already reflects any URL filters. The sidebar
  // never reads or writes the URL itself — writes happen on apply/reset and via
  // useTableInit's reflect effect — which removes the old read/write race.
  syncFiltersFromPreset(
   currentColumnsPreset?.filters ?? null,
   (model) => apiRef.current.setFilterModel(model, 'upsertFilterItems'),
   { updateUrl: false },
  );
 }, [currentColumnsPreset, structure]);

 //  useEffect(() => {
 //   // how to prevent this from running on change from sidebar
 //   const unsubscribe = apiRef.current.subscribeEvent(
 //    'filterModelChange',
 //    (model) => {},
 //   );

 //   return () => {
 //    unsubscribe(); // Cleanup
 //   };
 //  }, [apiRef]);

 return {
  applyFilters,
  resetFilters,
  methods,
  columns,
  currentColumnsPreset,
  structure,
  isFormChanged,
 };
};

export default useFilterSidebar;
